// A ZIP writer — the other half of data/zip.ts, for writing an .xlsx.
//
// Same bargain as the reader: `CompressionStream('deflate-raw')` is the only
// hard part, the browser ships it, and the container around it is a few dozen
// lines of fixed-offset headers. No zip64 — an .xlsx gTables writes is capped
// far below 4 GB by the cell limit on the workbook itself.

import type { Bytes } from './zip';

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

async function deflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof CompressionStream === 'undefined') {
    throw new Error('This browser cannot compress files, so it cannot write .xlsx.');
  }
  const stream = new CompressionStream('deflate-raw');
  const writer = stream.writable.getWriter();
  // Written concurrently with the read, for the reason given in zip.ts.
  const pumped = (async () => {
    await writer.write(bytes as Bytes);
    await writer.close();
  })().catch(() => undefined);
  const chunks: Uint8Array[] = [];
  const reader = stream.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  await pumped;
  return concat(chunks);
}

function concat(chunks: readonly Uint8Array[]): Bytes {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}

/**
 * Build a zip from text and/or binary files, in the order given. `deflate`
 * false stores them uncompressed, which the reader's tests use to cover
 * method 0.
 *
 * A `Bytes` entry (an opaque template part being carried through untouched —
 * `connections.xml`, a theme, anything round-tripped by the linked-export
 * merge) round-trips byte for byte: it is never passed through
 * `TextEncoder`/`TextDecoder`, which would corrupt it the moment it contains
 * anything outside valid UTF-8 text.
 */
export async function writeZip(files: Record<string, string | Bytes>, deflate = true): Promise<Bytes> {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const [name, content] of Object.entries(files)) {
    const raw = typeof content === 'string' ? encoder.encode(content) : content;
    const body = deflate ? await deflateRaw(raw) : raw;
    const nameBytes = encoder.encode(name);
    const crc = crc32(raw);
    const method = deflate ? 8 : 0;

    const local = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    // Bit 11: the name is UTF-8. A sheet called "Résumé" is otherwise mojibake.
    lv.setUint16(6, 0x0800, true);
    lv.setUint16(8, method, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, body.length, true);
    lv.setUint32(22, raw.length, true);
    lv.setUint16(26, nameBytes.length, true);
    local.set(nameBytes, 30);
    parts.push(local, body);

    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, method, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, body.length, true);
    cv.setUint32(24, raw.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint32(42, offset, true);
    central.set(nameBytes, 46);
    centrals.push(central);

    offset += local.length + body.length;
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, centrals.length, true);
  ev.setUint16(10, centrals.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  return concat([...parts, ...centrals, eocd]);
}
